import { z } from 'zod';

/**
 * Zod schemas for runtime validation at system boundary
 */
export const AuthTokensSchema = z.object({
    accessToken: z.string().min(1, 'accessToken is required'),
    refreshToken: z.string().min(1, 'refreshToken is required'),
    expiresIn: z.number().positive('expiresIn must be positive'),
});

export const AuthUserSchema = z.object({
    id: z.string().min(1, 'id is required'),
    email: z.string().email('invalid email format'),
    name: z.string().nullish(),
    picture: z.string().url().or(z.literal('')).nullish(),
    roles: z.array(z.string()).nullish(),
});

/**
 * Core auth types derived from Zod schemas
 */
export type AuthTokens = z.infer<typeof AuthTokensSchema>;
export type AuthUser = z.infer<typeof AuthUserSchema>;

/**
 * Response types from auth-v2 backend
 */
export interface LoginResponse {
    user: AuthUser;
    tokens: AuthTokens;
    isNewUser?: boolean;
}

export interface RegistrationResponse {
    message: string;
    email: string;
    requiresVerification: boolean;
}

/**
 * Auth error type
 */
export interface AuthError {
    message: string;
    code?: string;
}

/**
 * Auth state type
 */
export interface AuthState {
    isLoading: boolean;
    isAuthenticated: boolean;
    user: AuthUser | null;
    error: AuthError | null;
}

/**
 * Result returned from OAuth login methods
 */
export interface OAuthLoginResult {
    accessToken: string;
}

/**
 * Context value exposed by AuthProvider
 */
export interface AuthContextValue {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: AuthUser | null;
    error: AuthError | null;
    loginWithPassword: (email: string, password: string) => Promise<LoginResponse>;
    loginWithRedirect: (options?: { appState?: { returnTo?: string } }) => Promise<void>;
    loginWithGoogle: () => Promise<OAuthLoginResult>;
    loginWithMicrosoft: () => Promise<OAuthLoginResult>;
    register: (email: string, password: string, name?: string) => Promise<RegistrationResponse>;
    resendVerification: (email: string) => Promise<{ message: string }>;
    verifyCode: (email: string, code: string) => Promise<LoginResponse>;
    logout: (options?: { returnTo?: string }) => Promise<void>;
    getAccessTokenSilently: (options?: { ignoreCache?: boolean }) => Promise<string>;
    clearError: () => void;
}
